import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";

export interface CredentialField {
  field: string;
  label: string;
  secret: boolean;
}

export interface Provider {
  id: string;
  name: string;
  active: boolean;
  adapter: string | null;
  auth_strategy: string | null;
  default_discovery_interval_sec: string | null;
  default_poll_interval_sec: string | null;
  supports_discovery: boolean;
  endpoints: Record<string, string>;
  credentials_schema: CredentialField[];
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

export interface CreateProviderData {
  name: string;
  active?: boolean;
  adapter?: string | null;
  auth_strategy?: string | null;
  default_discovery_interval_sec?: string | null;
  default_poll_interval_sec?: string | null;
  supports_discovery?: boolean;
  endpoints?: Record<string, string>;
  credentials_schema?: CredentialField[];
}

interface ProvidersMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface ProvidersStore {
  providers: Provider[];
  loading: boolean;
  error: string | null;
  meta?: ProvidersMeta;
  totalCount: number;
  countLoading: boolean;
  fetchProviders: (params?: {
    limit?: number;
    page?: number;
    name?: string;
    adapter?: string;
    active?: string;
  }) => Promise<void>;
  fetchProvidersCount: () => Promise<void>;
  fetchProvider: (id: string) => Promise<Provider>;
  createProvider: (data: CreateProviderData) => Promise<Provider>;
  updateProvider: (id: string, data: Partial<CreateProviderData>) => Promise<Provider>;
  deleteProvider: (id: string) => Promise<void>;
}

export const useProvidersStore = create<ProvidersStore>((set, get) => ({
  providers: [],
  loading: false,
  error: null,
  meta: undefined,
  totalCount: 0,
  countLoading: false,

  fetchProviders: async (params = {}) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/providerList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
      });
      set({ providers: data.data || [], meta: data.meta, loading: false });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        set({ loading: false });
        return;
      }
      set({ error: getError(err), loading: false });
    }
  },

  fetchProvidersCount: async () => {
    if (get().countLoading) return;
    set({ countLoading: true });
    try {
      const { data } = await api.get("/providerList", {
        params: { limit: 1, page: 1 },
      });
      set({ totalCount: data.meta?.totalItems ?? 0, countLoading: false });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        set({ countLoading: false });
        return;
      }
      set({ countLoading: false });
    }
  },

  fetchProvider: async (id: string) => {
    const { data } = await api.get("/provider", { params: { id } });
    return data.data;
  },

  createProvider: async (providerData) => {
    const { data } = await api.post("/provider", providerData);
    return data.data;
  },

  updateProvider: async (id: string, providerData) => {
    const { data } = await api.put("/provider", providerData, {
      params: { id },
    });
    return data.data;
  },

  deleteProvider: async (id: string) => {
    await api.delete("/provider", { params: { id } });
  },
}));
